import { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from "react";
import { useQuery, useMutation } from "convex/react";
import { convexApi } from "@/lib/convex-api";
import { shouldSkipConvexQueries } from "@/hooks/use-convex-data";
import { useAuth } from "@/lib/auth-provider";
import { getUserStorage } from "@/lib/user-storage";
import { useRegisterLoadingState, useConnectionRecoveryOptional } from "@/lib/connection-recovery";

// Grace period before treating auth loss as a true logout (ms)
// This prevents site data from being cleared on momentary auth flickers
const AUTH_LOGOUT_GRACE_PERIOD_MS = 3000;

// Routes where we should skip site queries (auth-related pages)
const AUTH_ROUTES = ["/app/login", "/app/sso-callback", "/app/oauth-complete"];

export interface Site {
  _id: string;
  name: string;
  slug?: string;
  organizationId?: string;
  userRole?: string;
  shiftSwapSettings?: {
    roleRestriction?: boolean;
    hourLimit?: number | null;
  };
}

export interface Organization {
  _id: string;
  name: string;
  primaryBrand?: "centretable" | "shiftcanvas";
}

// The getUserSites query returns full Site objects with userRole added
interface ConvexSiteWithRole {
  _id: string;
  name: string;
  slug?: string;
  organizationId?: string;
  userRole: "owner" | "manager" | "staff";
  isActive?: boolean;
  timezone?: string;
  currency?: string;
  shiftSwapSettings?: {
    roleRestriction?: boolean;
    hourLimit?: number | null;
  };
}

interface SiteContextType {
  sites: Site[];
  organizations: Organization[];
  currentSite: Site | null;
  currentSiteId: string | null;
  setCurrentSiteId: (siteId: string) => void;
  isLoading: boolean;
  hasSites: boolean;
  resetContext: () => void;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export function SiteProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const userId = user?.id || null;
  const previousUserIdRef = useRef<string | null>(null);
  
  const recovery = useConnectionRecoveryOptional();
  const isResuming = recovery?.wasRecentlyResumed ?? false;
  
  const userStorage = useMemo(() => getUserStorage(userId), [userId]);
  
  const ensureAccessMutation = useMutation(convexApi.users.ensureUserSiteAccess);
  const [syncedForUserId, setSyncedForUserId] = useState<string | null>(null);
  
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(() => {
    if (!userId) return null;
    return userStorage.getSiteId();
  });
  
  // Track whether we've attempted to restore from storage - prevents premature auto-select
  const [hasAttemptedRestore, setHasAttemptedRestore] = useState(false);
  
  // Reset selected site when user changes (UserChangeGuard handles session data clearing)
  useEffect(() => {
    if (userId !== previousUserIdRef.current) {
      if (previousUserIdRef.current !== null) {
        console.log("[SiteContext] User changed, resetting site selection");
        setSelectedSiteId(null);
      }
      previousUserIdRef.current = userId;
    }
  }, [userId]);
  
  // Auto-sync: On first login, call ensureUserSiteAccess to auto-link any
  // staffMembers records that match the user's email (creates siteUsers access records)
  useEffect(() => {
    if (isAuthenticated && !authLoading && userId && syncedForUserId !== userId) {
      console.log("[SiteContext] Running ensureUserSiteAccess for user:", userId);
      ensureAccessMutation({ email: user?.email || undefined, userId: userId })
        .then((result) => {
          console.log("[SiteContext] ensureUserSiteAccess complete:", result);
          setSyncedForUserId(userId);
        })
        .catch((err) => {
          console.warn("[SiteContext] ensureUserSiteAccess failed (backend may not be ready):", err);
          setSyncedForUserId(userId);
        });
    }
  }, [isAuthenticated, authLoading, userId, user?.email, syncedForUserId, ensureAccessMutation]);
  
  // When user logs out, reset selected site - with grace period for auth flickers
  // This prevents data loss during token refreshes or momentary auth state changes
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (!isAuthenticated && previousUserIdRef.current) {
      // Start a grace period timer - only clear if auth stays false
      console.log("[SiteContext] Auth state lost, starting grace period before clearing site");
      
      logoutTimeoutRef.current = setTimeout(() => {
        console.log("[SiteContext] Grace period expired, clearing selected site (true logout)");
        setSelectedSiteId(null);
      }, AUTH_LOGOUT_GRACE_PERIOD_MS);
    } else if (isAuthenticated && logoutTimeoutRef.current) {
      // Auth recovered before grace period ended - cancel the clear
      console.log("[SiteContext] Auth recovered, cancelling site clear");
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    
    return () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    };
  }, [isAuthenticated]);
  
  // Check if we're on an auth-related page
  const isAuthRoute = typeof window !== "undefined" && AUTH_ROUTES.some(route => 
    window.location.pathname === route || window.location.pathname.startsWith(route + "/")
  );
  
  // Skip queries if: shouldSkipConvexQueries returns true, OR user isn't authenticated, OR we're on auth routes
  const skipQueries = shouldSkipConvexQueries() || !isAuthenticated || isAuthRoute || authLoading;
  
  // Pass email so Convex can look up sites even without JWT auth (Kinde session-based)
  const userEmail = user?.email || undefined;

  // Query for all organizations the user has access to
  const organizationsData = useQuery(
    convexApi.organizations.getAll,
    skipQueries ? "skip" : { email: userEmail }
  ) as Organization[] | undefined;

  // Query for all sites the user has access to (includes staff-linked sites)
  // This returns full Site objects with userRole added - where user is owner, manager, or staff
  const userSitesData = useQuery(
    convexApi.organizations.getUserSites,
    skipQueries ? "skip" : { email: userEmail }
  ) as ConvexSiteWithRole[] | undefined;
  
  // Map to Site format (query returns full site objects, just need to ensure correct typing)
  // null = Convex could not authenticate the caller (stale/expired token) —
  // same as undefined here: still loading, not "no sites".
  const allSitesData = useMemo((): Site[] | undefined => {
    if (userSitesData == null) return undefined;
    return userSitesData.map(site => ({
      _id: site._id,
      name: site.name,
      slug: site.slug,
      organizationId: site.organizationId,
      userRole: site.userRole,
      shiftSwapSettings: site.shiftSwapSettings,
    }));
  }, [userSitesData]);
  
  // Determine the effective site ID
  // Auto-selects when user has exactly one site; otherwise user must choose
  const effectiveSiteId = useMemo(() => {
    // Only return the selected site if it exists in user's accessible sites
    if (selectedSiteId && allSitesData?.some(s => s._id === selectedSiteId)) {
      return selectedSiteId;
    }
    // During resume, Convex may briefly deliver an empty array before real data syncs.
    // If we have a stored selection and allSitesData is empty, trust the stored value
    // rather than prematurely returning null and showing "no sites".
    if (selectedSiteId && allSitesData && allSitesData.length === 0) {
      console.log("[SiteContext] Sites array empty with active selection - treating as still loading (likely post-resume sync)");
      return selectedSiteId;
    }
    // If selected site is not accessible (non-empty list doesn't contain it), return null
    if (selectedSiteId && allSitesData && allSitesData.length > 0 && !allSitesData.some(s => s._id === selectedSiteId)) {
      console.warn("[SiteContext] Selected site not in user's accessible sites - returning null");
      return null;
    }
    // No selection or no data - return null (user must explicitly choose)
    return null;
  }, [selectedSiteId, allSitesData]);
  
  // Find the current site data from allSites
  const currentSite = useMemo((): Site | null => {
    if (!effectiveSiteId || !allSitesData) return null;
    return allSitesData.find(s => s._id === effectiveSiteId) || null;
  }, [effectiveSiteId, allSitesData]);
  
  // Restore selectedSiteId from userStorage on first load (after user is known)
  // NO auto-selection - only restore what user previously selected
  useEffect(() => {
    if (userId && allSitesData) {
      const storedSiteId = userStorage.getSiteId();
      console.log("[SiteContext] Restore attempt - stored:", storedSiteId, "current:", selectedSiteId, "sitesCount:", allSitesData.length, "isResuming:", isResuming);
      
      if (storedSiteId) {
        // We have a stored selection - validate it
        if (allSitesData.some(s => s._id === storedSiteId)) {
          // Stored site exists in user's sites - restore it
          if (!selectedSiteId || selectedSiteId !== storedSiteId) {
            console.log("[SiteContext] Restoring site from storage:", storedSiteId);
            setSelectedSiteId(storedSiteId);
          }
        } else if (allSitesData.length === 0) {
          // Sites array is empty - this is likely a transient state during Convex reconnection
          // after app resume. Do NOT clear the stored site; trust it until we get real data.
          console.log("[SiteContext] Sites array empty during restore - keeping stored site (likely post-resume sync)");
          if (!selectedSiteId) {
            setSelectedSiteId(storedSiteId);
          }
        } else {
          // Non-empty sites list genuinely doesn't contain the stored site.
          // But if we're actively resuming from background, still wait - the data
          // might be arriving in stages.
          if (isResuming) {
            console.log("[SiteContext] Stored site not found but app is resuming - deferring clear");
          } else {
            console.warn("[SiteContext] Stored site not accessible - clearing storage");
            userStorage.clearSiteId();
            setSelectedSiteId(null);
          }
        }
      } else if (allSitesData.length > 0) {
        // No stored selection — auto-select the first available site
        console.log("[SiteContext] No stored selection, auto-selecting first site:", allSitesData[0]._id, `(${allSitesData.length} sites available)`);
        const firstSiteId = allSitesData[0]._id;
        userStorage.setSiteId(firstSiteId);
        setSelectedSiteId(firstSiteId);
      }
      
      if (!hasAttemptedRestore) {
        setHasAttemptedRestore(true);
      }
    }
  }, [userId, allSitesData, selectedSiteId, userStorage, hasAttemptedRestore, isResuming]);
  
  const setCurrentSiteId = (siteId: string) => {
    console.log("[SiteContext] Switching to site:", siteId);
    userStorage.setSiteId(siteId);
    setSelectedSiteId(siteId);
  };
  
  const resetContext = () => {
    console.log("[SiteContext] Resetting context");
    setSelectedSiteId(null);
    userStorage.clearAll();
  };
  
  // When queries are skipped (satellite domains), don't show loading forever
  const isLoading = !skipQueries && allSitesData === undefined;

  const hasSites = (allSitesData?.length ?? 0) > 0;
  
  // Register loading state for connection recovery monitoring
  useRegisterLoadingState("site-context", isLoading);
  
  return (
    <SiteContext.Provider
      value={{
        sites: allSitesData || [],
        organizations: organizationsData || [],
        currentSite,
        currentSiteId: effectiveSiteId,
        setCurrentSiteId,
        isLoading,
        hasSites,
        resetContext,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error("useSite must be used within a SiteProvider");
  }
  return context;
}
